import './App.css';
import React, { useEffect, useState, useRef } from 'react';
import { useDebouncedEffect } from './useDebouncedEffect';

import { useQueryParam, StringParam } from 'use-query-params';

import { Input, AutoComplete } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Popover, Button } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import ForceGraph2D from 'react-force-graph-2d';

import { renderText, renderOptions } from './renderers';
import logo from './logo.png';

import { Helmet } from 'react-helmet';

const URL = '/.netlify/functions/getLyricsGraph';

const convertGraph = ({ nodes, edges: links }) => {
  nodes.forEach((node) => {
    node.id = node.key;
    node.val = node.attributes?.weight;
  });
  links.forEach((link) => {
    link.val = link.attributes?.weight;
  });
  return { nodes, links };
};

const doFetch = async (body) => {
  return fetch(`${URL}`, {
    method: 'POST',
    body: JSON.stringify(body),
  }).then((response) => response.json());
};

const App = () => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);
  let resizeWindow = () => {
    setWindowWidth(window.innerWidth);
    setWindowHeight(window.innerHeight);
  };

  useEffect(() => {
    resizeWindow();
    window.addEventListener('resize', resizeWindow);
    return () => window.removeEventListener('resize', resizeWindow);
  }, []);

  const [graph, setGraph] = useState({ nodeId: 'key', nodes: [], links: [] });
  const [searchTerm, setSearchTerm] = useState('');
  const [value, setValue] = useState('');
  const [options, setOptions] = useState([]);

  const [inputValue, setInputValue] = React.useState('');

  const [song, setSong] = useQueryParam('s', StringParam);
  const [id, setId] = useQueryParam('id', StringParam);

  const [image, setImage] = useState();
  const [loading, setLoading] = useState(false);

  const [url, setUrl] = useState('');

  const [pageTitle, setPageTitle] = useState('Song');

  const [infoBoxVisible, setInfoBoxVisible] = useState(true);

  const handleVisibleChange = (visible) => {
    setInfoBoxVisible(!infoBoxVisible);
  };

  useEffect(() => {
    if (searchTerm.length > 0) {
      doFetch({ action: 'getLyrics', id: searchTerm }).then(
        ({ graph, albumArt }) => {
          if (graph?.nodes) {
            setImage(albumArt);
            setGraph(convertGraph(graph));
          }
        }
      );
    }
  }, [searchTerm]);

  useEffect(() => {
    if (song) {
      setInputValue(song);
      doFetch({ action: 'getFirstResult', searchTerm: song }).then(
        ({ graph, option, albumArt }) => {
          if (graph?.nodes) {
            setGraph(convertGraph(graph));
            setOptions(renderOptions([option]));
            setValue(option.id);
            setImage(albumArt);
            setSong(undefined);
            setId(option.id);
          }
        }
      );
    }
  }, [song, setSong, setId]);

  useEffect(() => {
    if (id) {
      setLoading(true);
      doFetch({ action: 'getLyrics', id }).then((result) => {
        const { graph, title, full_title, artist, albumArt } = result;
        const searchTitle = title + ' ' + artist;
        if (graph?.nodes) {
          setUrl(result.url);
          setGraph(convertGraph(graph));
          setOptions(renderOptions([{ id, title }]));
          setValue(searchTitle);
          setInputValue(full_title);
          setPageTitle(full_title);
          setImage(albumArt);
          setLoading(false);
        }
      });
    }
  }, [id]);

  useDebouncedEffect(
    () => {
      if (value.length > 2) {
        doFetch({ action: 'getAutoComplete', searchTerm: value }).then(
          (data) => {
            if (data) {
              setOptions(renderOptions(data));
            }
          }
        );
      }
    },
    500,
    [value]
  );
  const forceRef = useRef(null);

  useEffect(() => {
    forceRef.current.d3Force('charge').strength(-100);
  });

  const style = {
    width: '30px',
    height: '30px',
    margin: '5px',
    fontSize: '30px',
  };

  const getLoading = (loading) =>
    loading ? (
      <LoadingOutlined spin style={style} />
    ) : (
      <span onMouseUp={(e) => e.stopPropagation()}>
        <a href={`${url}`} target="_blank" rel="noopener noreferrer">
          <img
            className="fade-in"
            alt=""
            src={image}
            style={!image ? {} : { ...style, zIndex: 1000 }}
          />
        </a>
      </span>
    );

  return (
    <div className="App" style={{ height: windowHeight, width: windowWidth }}>
      <Helmet>
        <title>{pageTitle} Lyrics Graph ~ source/target</title>
      </Helmet>
      <AutoComplete
        autoFocus
        backfill
        dropdownClassName="certain-category-search-dropdown"
        dropdownMatchSelectWidth={500}
        value={inputValue}
        options={options}
        onSelect={(val, option) => {
          if (option) {
            setInputValue(val);
            setId(option.key);
            setSong(undefined);
          }
        }}
      >
        <Input.Search
          allowClear
          prefix={<img alt="" src={logo} style={style} />}
          suffix={getLoading(loading)}
          size="large"
          placeholder="Search for song..."
          onSearch={(e) => setSearchTerm(value)}
          onChange={(e) => {
            setValue(e.target.value);
            setInputValue(e.target.value);
          }}
        />
      </AutoComplete>
      <Popover
        placement="leftTop"
        content={
          <>
            <a href="https://www.cjlm.ca/posts/source-target-song-lyrics/">
              Learn more&nbsp;
            </a>
            & sign up for the newsletter{' '}
            <a href="https://www.cjlm.ca/source/target">here</a>
            .
            <br />
            Suggestions or comments?{' '}
            <a href="mailto:%63%68%72%69%73%74%69%61%6E%40%63%6A%6C%6D%2E%63%61">
              Email&nbsp;
            </a>
            or follow me on <a href="https://twitter.com/cjlmiles">Twitter</a>.
          </>
        }
        // title="Created by Christian Miles for source/target"
        title={
          <>
            Created by Christian Miles for <b>source/target</b>
          </>
        }
        trigger="click"
        visible={infoBoxVisible}
        onVisibleChange={handleVisibleChange}
      >
        <Button
          style={{
            background: 'rgb(146, 243, 200)',
            border: 'rgb(58, 58, 58)',
            marginTop: '5px',
            position: 'absolute',
            right: '5px',
            bottom: '5px',
            zIndex: 1000,
          }}
          color="red"
          type="primary"
          shape="circle-outline"
          icon={
            <InfoCircleOutlined
              style={{
                color: 'rgb(58, 58, 58)',
              }}
            />
          }
        ></Button>
      </Popover>
      <ForceGraph2D
        style={{ position: 'absolute', margin: 0 }}
        height={windowHeight}
        width={windowWidth}
        linkDirectionalArrowLength={4}
        linkDirectionalArrowRelPos={1}
        graphData={graph}
        nodeCanvasObject={renderText}
        nodeCanvasObjectMode={() => 'after'}
        nodeColor={() => 'rgba(0,0,0,0)'}
        nodeVal={(node) => Math.min(40, node.val)}
        linkWidth={(link) => Math.min(30, link.val)}
        ref={forceRef}
      />
    </div>
  );
};

export default App;
